import { validate as validateUuid } from 'uuid';
import {
	ChatRef,
	DesignSystemVersion,
	SceneComment,
	SceneDsLink,
} from 'dragonmouse-shared/interfaces';
import { ScenePollResponse } from 'dragonmouse-shared/poll';
import { VITE_FORGE_URL } from '@constants';
import {
	clearActiveScene,
	setSceneLoadError,
	setSceneUserContacts,
	updateSceneChatRefs,
	updateSceneComments,
	resolveOptimisticallyUpdatedSceneComments,
	updateActiveSceneDsLink,
	updateActiveSceneDesignSystemModes,
} from '../redux/db/actions';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

// Processes date fields on a DesignSystemVersion from JSON
const processVersion = (v: DesignSystemVersion): DesignSystemVersion => ({
	...v,
	createdAt: new Date(v.createdAt),
	updatedAt: new Date(v.updatedAt),
});

export class ScenePollingStream extends PollingStream<ScenePollResponse> {
	constructor(activeSceneId: string) {
		// Validate UUIDs before constructing URL
		if (!validateUuid(activeSceneId)) {
			throw new Error(`Invalid scene ID: ${activeSceneId}`);
		}

		const url = VITE_FORGE_URL + `/poll/scene/${activeSceneId}`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setSceneLoadError('You do not have access to this project'));
				store.dispatch(clearActiveScene());
			} else {
				console.error(error);
			}
		};

		super(url, onError, 'activeScene.chatRefs');
	}

	extractData(response: ScenePollResponse | null): ScenePollResponse[] {
		return response ? [response] : [];
	}

	onData(responses: ScenePollResponse[]) {
		const response = responses.length > 0 ? responses[0] : null;
		if (!response) {
			store.dispatch(clearActiveScene());
			return;
		}

		// Update chat refs
		const processedChatRefs: ChatRef[] = (response.chatRefs || []).map((ref) => ({
			...ref,
			updatedAt: new Date(ref.updatedAt),
		}));
		store.dispatch(updateSceneChatRefs(processedChatRefs));

		// Update scene comments
		const processedComments: SceneComment[] = (response.comments || []).map((c) => ({
			...c,
			createdAt: new Date(c.createdAt),
			updatedAt: c.updatedAt ? new Date(c.updatedAt) : undefined,
			deletedAt: c.deletedAt ? new Date(c.deletedAt) : null,
		}));
		const commentWriteIds = processedComments
			.map((c) => c.writeId)
			.filter((writeId): writeId is string => !!writeId);
		if (commentWriteIds.length > 0) {
			store.dispatch(resolveOptimisticallyUpdatedSceneComments(commentWriteIds));
		}
		store.dispatch(updateSceneComments(processedComments));

		// Update the scene's user roster (sharing + mentions)
		store.dispatch(setSceneUserContacts(response.userContacts ?? []));

		// Update design system link
		if (response.sceneDsLink) {
			const raw = response.sceneDsLink;
			const dsLink: SceneDsLink = {
				designSystem: {
					...raw.designSystem,
					createdAt: new Date(raw.designSystem.createdAt),
					updatedAt: new Date(raw.designSystem.updatedAt),
				},
				linkedVersion: processVersion(raw.linkedVersion),
				latestPublishedVersion: processVersion(raw.latestPublishedVersion),
				hasDraft: raw.hasDraft,
			};
			store.dispatch(updateActiveSceneDsLink(dsLink));
		} else {
			store.dispatch(updateActiveSceneDsLink(null));
		}

		// Update modes (from linked DS config/display.json)
		if (response.dsModes && response.dsModes.length > 0) {
			store.dispatch(updateActiveSceneDesignSystemModes(response.dsModes));
		} else {
			store.dispatch(updateActiveSceneDesignSystemModes(null));
		}
	}
}
